import { Link } from 'react-router-dom';

export default function SEOContent() {
  return (
    <>
      {/* Social Proof */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-10">
            Trusted Resource For International Shopping Research
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { number: '1,000+', label: 'Product Guides', icon: 'ri-book-open-line' },
              { number: '60+', label: 'Expert Articles', icon: 'ri-article-line' },
              { number: '100+', label: 'SEO Resources', icon: 'ri-seo-line' },
            ].map((stat) => (
              <div key={stat.label} className="p-6 bg-white rounded-xl border border-background-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-1">{stat.number}</div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Is Sugargoo Spreadsheet */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
            What Is <strong>Sugargoo Spreadsheet</strong>?
          </h2>
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 mb-10">
            <p className="mb-4 leading-relaxed">
              A <strong>Sugargoo Spreadsheet</strong> is an organized database that savvy international shoppers use to navigate the vast world of Chinese marketplaces. Instead of spending hours scrolling through endless product listings, a well-structured Sugargoo Spreadsheet gives you instant access to curated product links, detailed item information, pricing comparisons, and quality references — all organized by category for effortless browsing.
            </p>
            <p className="mb-4 leading-relaxed">
              Thousands of shoppers worldwide rely on <strong>Sugargoo Spreadsheet</strong> resources to streamline their shopping experience. Whether you're hunting for the latest sneaker releases, trendy streetwear pieces, luxury accessories, or everyday essentials, these spreadsheets transform chaotic browsing into a structured, time-saving process. The Sugargoo Spreadsheet community has grown rapidly because it eliminates the guesswork — you can see exactly what other buyers have found, verified, and recommended.
            </p>
            <p className="mb-4 leading-relaxed">
              For beginners, starting with a <strong>Sugargoo Spreadsheet</strong> is the smartest way to begin international shopping. Instead of learning through trial and error, you get a pre-organized collection of trusted product links, complete with notes on sizing, quality, and seller reliability. This dramatically reduces the learning curve and helps you avoid common first-time mistakes like ordering wrong sizes or choosing unreliable sellers.
            </p>
            <p className="leading-relaxed">
              The best <strong>Sugargoo Spreadsheet</strong> resources are continuously updated, reflecting the latest trends, seasonal collections, and community discoveries. By using a regularly maintained spreadsheet, you stay ahead of the curve and never miss out on trending items or limited releases that sell out quickly.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto rounded-xl border border-background-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Feature</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-500">Normal Searching</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-accent-700 bg-accent-50">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Finding Products', normal: 'Slow', spreadsheet: 'Fast' },
                  { feature: 'Categories', normal: 'Random', spreadsheet: 'Organized' },
                  { feature: 'Research Time', normal: 'Hours', spreadsheet: 'Minutes' },
                  { feature: 'Beginner Friendly', normal: 'Low', spreadsheet: 'High' },
                ].map((row) => (
                  <tr key={row.feature} className="border-t border-background-100">
                    <td className="px-5 py-3 font-medium text-foreground-800">{row.feature}</td>
                    <td className="px-5 py-3 text-foreground-500">{row.normal}</td>
                    <td className="px-5 py-3 text-accent-700 font-medium bg-accent-50/50">{row.spreadsheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
            Why Choose <strong>Sugargoo Spreadsheet</strong>?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Save Time', desc: 'Skip hours of manual searching. Our Sugargoo Spreadsheet organizes thousands of products so you find what you need in seconds.', icon: 'ri-timer-line' },
              { title: 'Find Trending Products', desc: 'Stay ahead with updated Sugargoo Spreadsheet collections featuring the latest trending sneakers, streetwear, and accessories.', icon: 'ri-fire-line' },
              { title: 'Compare Options', desc: 'Easily compare products side by side with organized Sugargoo Spreadsheet data including prices, sizes, and seller ratings.', icon: 'ri-scales-line' },
              { title: 'Avoid Beginner Mistakes', desc: 'New to Sugargoo? Our Sugargoo Spreadsheet guides help you avoid common pitfalls like incorrect sizing and unreliable sellers.', icon: 'ri-shield-check-line' },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 bg-white rounded-xl border border-background-200 hover:border-accent-200 transition-colors duration-200">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} text-lg md:text-xl`}></i>
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sugargoo Reviews */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
            Sugargoo Review: What Buyers Should Know
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-white rounded-xl border border-accent-200 bg-accent-50/30">
              <h3 className="text-lg font-heading font-semibold text-accent-800 mb-4 flex items-center gap-2">
                <i className="ri-thumb-up-line"></i> Pros
              </h3>
              <ul className="space-y-2.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i> Large product selection across multiple Chinese marketplaces</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i> Reliable warehouse service with quality inspection</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i> Multiple international shipping options available</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i> Active community sharing Sugargoo Spreadsheet resources</li>
              </ul>
            </div>
            <div className="p-6 bg-white rounded-xl border border-background-200">
              <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                <i className="ri-information-line"></i> Cons
              </h3>
              <ul className="space-y-2.5 text-sm text-foreground-700">
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-400 mt-0.5"></i> Shipping prices vary significantly by method and destination</li>
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-400 mt-0.5"></i> Users need to do their own research before making purchases</li>
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-400 mt-0.5"></i> Returns and exchanges can be complicated for international orders</li>
              </ul>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link
              to="/sugargoo-spreadsheet-guide"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 text-white font-semibold text-sm rounded-full hover:bg-secondary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-article-line"></i>
              Read Complete Sugargoo Review
            </Link>
          </div>
        </div>
      </section>

      {/* Shipping Cost Guide */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
            Sugargoo Shipping Cost Explained
          </h2>
          <div className="overflow-x-auto rounded-xl border border-background-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Shipping Method</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Speed</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Cost Level</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Best For</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { method: 'Air Shipping', speed: 'Fast (5-10 days)', cost: 'High', best: 'Urgent Orders' },
                  { method: 'Sea Shipping', speed: 'Slow (30-45 days)', cost: 'Low', best: 'Large Packages' },
                  { method: 'Express (DHL/FedEx)', speed: 'Fast (3-7 days)', cost: 'Medium', best: 'Small Orders' },
                  { method: 'Economy Line', speed: 'Moderate (10-20 days)', cost: 'Low-Medium', best: 'Budget-Conscious Orders' },
                ].map((row) => (
                  <tr key={row.method} className="border-t border-background-100">
                    <td className="px-5 py-3 font-medium text-foreground-800">{row.method}</td>
                    <td className="px-5 py-3 text-foreground-600">{row.speed}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        row.cost === 'High' ? 'bg-primary-100 text-primary-700' :
                        row.cost === 'Low' ? 'bg-accent-100 text-accent-700' :
                        'bg-secondary-100 text-secondary-700'
                      }`}>{row.cost}</span>
                    </td>
                    <td className="px-5 py-3 text-foreground-600">{row.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Coupon & Discount */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
            Sugargoo Coupon & Discount Code Guide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: 'How Coupons Work', desc: 'Sugargoo offers various discount codes and coupons that can be applied during checkout to reduce your total order cost. These codes are often seasonal or tied to specific promotions.', icon: 'ri-coupon-line' },
              { title: 'Where to Find Discounts', desc: 'Keep an eye on the Sugargoo Spreadsheet community, official announcements, and social media channels. Many discount codes are shared within the shopping community.', icon: 'ri-search-eye-line' },
              { title: 'How to Save Money', desc: 'Combine coupons with optimized shipping methods and bulk ordering. Using a Sugargoo Spreadsheet helps you plan purchases strategically for maximum savings.', icon: 'ri-money-dollar-circle-line' },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 bg-white rounded-xl border border-background-200">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className={`${item.icon} text-lg md:text-xl`}></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            Is Sugargoo Legit?
          </h2>
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 text-left mb-8">
            <p className="mb-4 leading-relaxed">
              Sugargoo is a legitimate shopping agent platform that helps international customers purchase products from Chinese marketplaces. As a registered business operating in China, Sugargoo provides warehouse storage, quality control inspection, and international shipping services that have been used by thousands of customers worldwide.
            </p>
            <p className="mb-4 leading-relaxed">
              However, like any international shopping experience, users should always verify the following before making purchases: seller reputation and ratings, product quality through QC photos, shipping fees for your specific destination, and the platform's return and exchange policies.
            </p>
            <div className="bg-white p-5 rounded-xl border border-background-200 mt-6">
              <p className="text-xs md:text-sm text-foreground-500 italic flex items-start gap-2">
                <i className="ri-information-line mt-0.5"></i>
                <span><strong>Disclaimer:</strong> This website provides educational information and independent resources about Sugargoo Spreadsheet. We are not affiliated with or endorsed by Sugargoo. All product links redirect to third-party platforms. Always conduct your own research before making purchasing decisions.</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}